import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Loader2, MapPin, Search, Plus, Phone, ArrowLeft, ExternalLink, X, Shield,
  ParkingSquare, Wrench, Sparkles as SparkIcon, Bug, Droplet, Store,
  Package, Cpu, ShoppingBag,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const VERTICAL_META = {
  parking:          { label: "Near Parking",           icon: ParkingSquare, tag: "Book by hour",     accent: "from-sky-600 to-blue-800" },
  equipment_rental: { label: "Equipment & Tool Rent",  icon: Wrench,        tag: "Book by day",      accent: "from-orange-600 to-red-700" },
  cleaning:         { label: "Commercial Cleaning",    icon: SparkIcon,     tag: "Book a visit",     accent: "from-emerald-600 to-teal-800" },
  pest_control:     { label: "Pest Control",           icon: Bug,           tag: "Book a visit",     accent: "from-amber-600 to-orange-800" },
  laundry:          { label: "Commercial Laundry",     icon: ShoppingBag,   tag: "Pickup/kg",        accent: "from-cyan-600 to-blue-800" },
  water_ro:         { label: "RO Water Delivery",      icon: Droplet,       tag: "Per can",          accent: "from-blue-500 to-cyan-700" },
  vending_machine:  { label: "Vending Machine",        icon: Package,       tag: "Monthly rent",     accent: "from-fuchsia-600 to-purple-800" },
  saas_micro:       { label: "Micro-SaaS Directory",   icon: Cpu,           tag: "Software monthly", accent: "from-slate-700 to-stone-900" },
  liquor_bar:       { label: "Liquor · Cigarettes · Bars", icon: Store,     tag: "Per order · 21+ only", accent: "from-red-700 to-rose-900", ageGate: true, hoursNote: "Delivery window: 10:00 – 23:00 IST (state law)." },
  medical_store:    { label: "Medical Store",          icon: SparkIcon,     tag: "Per order",        accent: "from-emerald-500 to-teal-800", allowsPrescription: true },
};

/**
 * /services/:vertical — unified provider directory + booking flow.
 * Single component powers 8 verticals (parking, cleaning, rental, etc.).
 */
export default function ServicesVertical() {
  const { vertical } = useParams();
  const meta = VERTICAL_META[vertical];
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [booking, setBooking] = useState(null);   // provider being booked

  useSeo({
    title: `${meta?.label || "Services"} · find2near`,
    description: `Discover ${meta?.label || "services"} providers near you. Book instantly with secure Stripe checkout.`,
  });

  const load = async () => {
    if (!meta) return;
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q)    params.q    = q;
      if (city) params.city = city;
      const r = await api.get(`/services/${vertical}/providers`, { params });
      setRows(r.data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vertical]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, city]);

  if (!meta) return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="text-center">
        <div className="font-black text-xl text-stone-900">Unknown service category</div>
        <Link to="/apps" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-fuchsia-700 hover:underline"><ArrowLeft size={13}/> Back</Link>
      </div>
    </div>
  );

  const Icon = meta.icon;

  return (
    <div className="min-h-screen bg-stone-50" data-testid={`svc-page-${vertical}`}>
      <div className={`relative h-40 bg-gradient-to-br ${meta.accent} overflow-hidden`}>
        <div className="relative max-w-4xl mx-auto px-4 py-6 text-white h-full flex flex-col justify-end">
          <div className="text-[11px] uppercase tracking-widest font-black opacity-80 flex items-center gap-1"><Icon size={12}/> {meta.tag}</div>
          <h1 className="heading text-3xl md:text-4xl font-black mt-1 drop-shadow">{meta.label}</h1>
        </div>
      </div>

      {meta.ageGate && (
        <div className="bg-rose-100 border-b border-rose-300 text-rose-900 text-xs font-black text-center py-2" data-testid="svc-age-gate-banner">
          ⚠️ 21+ only. Your DOB in profile is verified at checkout. Consume responsibly.
          {meta.hoursNote && <span className="ml-2 opacity-80">· {meta.hoursNote}</span>}
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or service…" className="pl-8 rounded-full" data-testid="svc-search"/>
          </div>
          <div className="relative w-40">
            <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input value={city} onChange={e => setCity(e.target.value)} placeholder="City" className="pl-8 rounded-full" data-testid="svc-city"/>
          </div>
          {user && (
            <Button onClick={() => setShowAdd(true)} className={`rounded-full bg-gradient-to-br ${meta.accent} text-white font-black`} data-testid="svc-list-btn">
              <Plus size={13} className="mr-1"/> List your business
            </Button>
          )}
        </div>

        {loading ? (
          <div className="p-12 text-center"><Loader2 className="inline animate-spin text-stone-400" size={24}/></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-stone-500">
            No providers yet. {user ? "Be the first — click 'List your business'." : "Sign in to be the first to list."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {rows.map(p => (
              <div key={p.provider_id} className="bg-white rounded-3xl border border-stone-200 overflow-hidden" data-testid={`svc-card-${p.provider_id}`}>
                {p.photo_url && <img src={p.photo_url} alt="" className="w-full h-32 object-cover"/>}
                <div className="p-3 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-black text-stone-900 flex items-center gap-1">
                      {p.name}
                      {p.verified && (
                        <Shield size={13} className="text-emerald-600" data-testid={`svc-verified-${p.provider_id}`} title="Verified provider — trusted by Find2Near"/>
                      )}
                    </div>
                    <div className="text-sm font-black text-emerald-700 shrink-0">₹{p.price_inr}<span className="text-[10px] font-normal text-stone-500">/{meta.tag.replace(/^\S+\s*/, "").toLowerCase()}</span></div>
                  </div>
                  {p.city && <div className="text-[11px] text-stone-500 flex items-center gap-1"><MapPin size={11}/> {p.city}{p.distance_km != null && <span className="ml-1">· {p.distance_km} km</span>}</div>}
                  {p.description && <p className="text-xs text-stone-600 line-clamp-2">{p.description}</p>}
                  {(p.services || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {p.services.slice(0, 4).map((s, i) => (
                        <span key={i} className="text-[10px] font-bold bg-stone-100 text-stone-700 rounded-full px-2 py-0.5">{s}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    {p.phone && (
                      <a href={`tel:${p.phone}`} className="text-[11px] font-black text-stone-700 hover:text-emerald-700 inline-flex items-center gap-1" data-testid={`svc-call-${p.provider_id}`}>
                        <Phone size={11}/> Call
                      </a>
                    )}
                    {user && user.user_id === p.owner_user_id && !p.verified && (
                      <button
                        onClick={async () => {
                          try {
                            const r = await api.post(`/services/${vertical}/providers/${p.provider_id}/verify`, { origin_url: window.location.origin });
                            if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
                          } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
                        }}
                        className="text-[11px] font-black text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                        data-testid={`svc-verify-${p.provider_id}`}
                        title="Get the Verified badge for ₹499/quarter"
                      >
                        <Shield size={11}/> Get verified
                      </button>
                    )}
                    <Button onClick={() => setBooking(p)} size="sm" className="ml-auto rounded-full bg-stone-900 hover:bg-stone-800 text-white text-[11px] font-black h-7" data-testid={`svc-book-${p.provider_id}`}>
                      Book
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pt-4 flex items-center justify-between text-[11px] text-stone-500">
          <span>{rows.length} providers</span>
          {user && <Link to={`/services/${vertical}/mine`} className="font-black text-stone-700 hover:underline">My listings & bookings →</Link>}
        </div>
      </div>

      {showAdd && <ListModal vertical={vertical} meta={meta} onClose={saved => { setShowAdd(false); if (saved) load(); }}/>}
      {booking && <BookModal vertical={vertical} meta={meta} provider={booking} onClose={() => setBooking(null)}/>}
    </div>
  );
}

function ListModal({ vertical, meta, onClose }) {
  const [f, setF] = useState({ name: "", city: "", price_inr: 100, services: "", description: "", phone: "", photo_url: "", availability: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !f.price_inr) { toast.error("Name + price required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        services: (f.services || "").split(",").map(x => x.trim()).filter(Boolean),
        price_inr: parseInt(f.price_inr) || 100,
      };
      await api.post(`/services/${vertical}/providers`, payload);
      toast.success("Listed successfully");
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-md space-y-3 max-h-[92vh] overflow-y-auto" data-testid="svc-list-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900">List — {meta.label}</h3>
          <button onClick={() => onClose(false)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>
        <Input placeholder="Business name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="rounded-full text-sm" data-testid="svc-list-name"/>
        <Input placeholder="City" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} className="rounded-full text-sm" data-testid="svc-list-city"/>
        <Input type="number" placeholder={`Price ₹ (${meta.tag.toLowerCase()})`} value={f.price_inr} onChange={e => setF({ ...f, price_inr: e.target.value })} className="rounded-full text-sm" data-testid="svc-list-price"/>
        <Input placeholder="Services (comma separated)" value={f.services} onChange={e => setF({ ...f, services: e.target.value })} className="rounded-full text-sm" data-testid="svc-list-services"/>
        <Input placeholder="Photo URL (optional)" value={f.photo_url} onChange={e => setF({ ...f, photo_url: e.target.value })} className="rounded-full text-sm"/>
        <Input placeholder="Phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} className="rounded-full text-sm"/>
        <Input placeholder="Availability (e.g. Mon-Sat 8am-8pm)" value={f.availability} onChange={e => setF({ ...f, availability: e.target.value })} className="rounded-full text-sm"/>
        <Textarea rows={3} placeholder="Description" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} className="rounded-2xl text-sm"/>
        <Button onClick={save} disabled={busy} className={`w-full rounded-full bg-gradient-to-br ${meta.accent} text-white font-black h-10`} data-testid="svc-list-save">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Plus size={14} className="mr-1"/>} List
        </Button>
      </div>
    </div>
  );
}

function BookModal({ vertical, meta, provider, onClose }) {
  const [qty, setQty] = useState(1);
  const [startAt, setStartAt] = useState("");
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState("stripe");
  const [prescriptionUrl, setPrescriptionUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const total = useMemo(() => qty * provider.price_inr, [qty, provider.price_inr]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/services/${vertical}/book`, {
        provider_id: provider.provider_id,
        qty: parseInt(qty) || 1,
        start_at: startAt || null,
        notes: notes || null,
        origin_url: window.location.origin,
        payment_mode: mode,
        prescription_url: prescriptionUrl || null,
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.success("Booking placed (COD) — provider will confirm.");
        onClose();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-sm space-y-3 max-h-[92vh] overflow-y-auto" data-testid="svc-book-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900">Book · {provider.name}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>
        <div className="text-xs text-stone-500">₹{provider.price_inr} {meta.tag.toLowerCase()} · {provider.city || "Any city"}</div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Quantity ({meta.tag.toLowerCase()})</div>
          <Input type="number" min={1} value={qty} onChange={e => setQty(e.target.value)} className="rounded-full text-sm" data-testid="svc-book-qty"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Start (optional)</div>
          <Input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="rounded-full text-sm" data-testid="svc-book-start"/>
        </div>
        <Textarea rows={2} placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} className="rounded-2xl text-sm"/>
        {meta.allowsPrescription && (
          <div className="rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-3 space-y-2">
            <div className="text-[10px] uppercase font-black text-emerald-800">Prescription upload (recommended)</div>
            <Input placeholder="Prescription image URL (jpg/png/pdf)" value={prescriptionUrl}
                   onChange={e => setPrescriptionUrl(e.target.value)} className="rounded-full text-sm bg-white" data-testid="svc-book-prescription"/>
            <div className="text-[10px] text-emerald-800/70">The pharmacy will verify the Rx before dispensing scheduled drugs.</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {["stripe", "cod"].map(m => (
            <button key={m} onClick={() => setMode(m)} className={`p-2 rounded-2xl border-2 text-xs font-black ${mode === m ? "border-stone-900 bg-stone-50" : "border-stone-200"}`} data-testid={`svc-book-mode-${m}`}>
              {m === "stripe" ? "Pay online" : "Cash on service"}
            </button>
          ))}
        </div>
        <Button onClick={submit} disabled={busy} className={`w-full rounded-full bg-gradient-to-br ${meta.accent} text-white font-black h-10`} data-testid="svc-book-submit">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <ExternalLink size={14} className="mr-1"/>} Book · ₹{total.toLocaleString("en-IN")}
        </Button>
      </div>
    </div>
  );
}
